<script>
    import AssetsApi from "../../../asset/assets.api.js";
    import BreadCrumNav from "../../../../components/breadcrum-nav.svelte";
    import PropertiesList from "../../../../components/properties-list.svelte";
    import PropValueRow from "../../../../components/powerTable/prop-value-row.svelte";
    import { ipAdressesCustomComparator } from "../../../../js/utils.js";
    import CountByPropValue from "../../_components/count-by-prop-value.svelte";
    import AssetCount from "../../../asset/_components/asset-count.svelte";
    import tagService from "../../../tag/service.js";
    import { params, goto } from "@roxi/routify";
    import { format } from "svelte-i18n";
    import { token } from "../../../user/user.state.svelte.js";

    // --- Vanilla JS Replacements ---
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";

    const redirect = $goto;

    let loc = $derived($params.value && $params.value !== "General" ? decodeURIComponent($params.value) : "");
    let refresh = $state(false);

    // Active filters driven strictly by the URL
    let currentFilter = $derived.by(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));
        const active = {};
        if (paramsCopy.asset__kind__in) active.asset__kind__in = paramsCopy.asset__kind__in;
        if (paramsCopy.asset_tag_id) active.asset_tag_id = paramsCopy.asset_tag_id;
        return active;
    });

    // SVELTE 5: Local UI state for form inputs before user clicks "Search"
    let filters = $state({
        asset__kind__in: [],
        asset_tag_id: ""
    });

    // Localize asset kinds for TomSelect
    let translatedAssetKinds = $derived(
        AssetsApi.assets_kinds.map((opt) => ({
            id: opt.id,
            name: $format(`asset.kind.${opt.id}.singular`)
        }))
    );

    // Sync URL $params to local UI state
    $effect(() => {
        const paramsCopy = JSON.parse(JSON.stringify($params || {}));

        let initialKind = paramsCopy.asset__kind__in || [];
        if (typeof initialKind === "string") {
            initialKind = initialKind.split(",");
        }

        filters = {
            asset__kind__in: initialKind,
            asset_tag_id: paramsCopy.asset_tag_id || ""
        };
    });

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return {
                        id: opt.id,
                        name: opt.name || opt.title_es || opt.title || opt.email || opt.id
                    };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    const handleEmptyFilters = (filtersObj = {}) => {
        const clean = {};
        for (let [key, val] of Object.entries(filtersObj)) {
            if (val !== "" && val !== null && val !== undefined) {
                clean[key] = val;
            }
        }
        return clean;
    };

    function handleFilterChange() {
        let updatedFilter = {};

        if (filters.asset__kind__in && filters.asset__kind__in.length > 0) {
            updatedFilter.asset__kind__in = filters.asset__kind__in.join(",");
        }

        if (filters.asset_tag_id) {
            updatedFilter.asset_tag_id = filters.asset_tag_id;
        }

        redirect("./", { ...updatedFilter, value: loc });
    }

    const clearFilters = () => {
        if (!currentFilter.asset__kind__in && !currentFilter.asset_tag_id) {
            return;
        }

        filters = { asset__kind__in: [], asset_tag_id: "" };
        redirect("./", { value: loc });
    };

    const handleAssetFilters = () => {
        let returnValue = {};
        if (currentFilter.asset_tag_id) {
            returnValue.tag_id = currentFilter.asset_tag_id;
        }
        if (currentFilter.asset__kind__in) {
            returnValue.kind__in = currentFilter.asset__kind__in;
        }
        return returnValue;
    };

    let assetFilters = $derived.by(() => handleAssetFilters());

    const getTags = async () => {
        if (!token.access_token) return [];
        return await tagService.search("v2/tag/", {});
    };

    let columns = [
        {
            key: "kind",
            title: $format("list.columns.kind"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "name",
            title: $format("list.columns.name"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "value",
            title: $format("list.columns.value"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropValueRow,
        },
        {
            key: "asset_name",
            title: $format("list.columns.asset"),
            filterable: false,
            parseAs: "unsafe-html",
            userFunctions: { customSort: ipAdressesCustomComparator },
        },
        {
            key: "asset_kind",
            title: $format("list.columns.asset_kind"),
            filterable: false,
            parseAs: "unsafe-html",
        },
    ];

    const parseRows = (pageContent) => {
        const filterParams = { ...currentFilter };
        const queryString = new URLSearchParams(filterParams).toString();
        const querySuffix = queryString ? `?${queryString}` : "";

        for (let row of pageContent) {
            row.value = {
                kind: row.kind,
                value: row.value,
                target_object_type: "asset",
                name: row.name,
                max_chars: 70,
                filter: filterParams,
                filters: filterParams,
                querySuffix: querySuffix,
            };
            if (row["name"]) {
                row["name"] =
                    `<a href="/property/asset/${encodeURIComponent(row.kind)}/${encodeURIComponent(row.name)}${querySuffix}">` +
                    row.name +
                    "</a>";
            }
            if (row["kind"]) {
                row["kind"] =
                    `<a href="/property/asset/${encodeURIComponent(row.kind)}${querySuffix}">` +
                    row.kind +
                    "</a>";
            }
            if (row["asset_name"]) {
                row["asset_name"] =
                    `<a href='/asset/${row.asset_kind}/${row["asset_id"]}'>${row.asset_name}</a>`;
            }
            if (row["asset_kind"]) {
                row["asset_kind"] = $format(
                    `asset.kind.${row.asset_kind}.singular`,
                );
            }
        }
        return pageContent;
    };

    const getData = async () => {
        if (!token.access_token) return { data: [], propsByKind: [], propsByKindAndName: [] };

        let dataConfig = { ...currentFilter, location: loc };
        if (dataConfig.value) {
            delete dataConfig.value;
        }

        let data = await AssetsApi.fetchAssets("v2/asset-property/", {
            filter: dataConfig,
        });

        let assetsConfig = { ...assetFilters };
        if (assetsConfig.value) {
            delete assetsConfig.value;
        }

        let assets = await AssetsApi.fetchAssets("v2/asset/", {
            filter: assetsConfig
        });

        let propsByKind = groupPropsByKind(data || []);
        let propsByKindAndName = groupPropsByKindAndName(data || []);

        let items = JSON.parse(JSON.stringify(data || []));
        for (let item of items) {
            let asset = assets.find((asset) => asset.id === item.asset_id);
            if (asset) {
                item.asset_kind = asset.kind;
                item.asset_name = asset.name;
            }
            item.location =
                item.location && item.location !== ""
                    ? item.location
                    : "General";
        }
        return {
            data: items,
            propsByKind: propsByKind,
            propsByKindAndName: propsByKindAndName,
        };
    };

    const groupPropsByKind = (props) => {
        let groupedProps = props.reduce((a, b) => {
            const found = a.find((e) => e.kind == b.kind);
            return (
                found
                    ? found.asset_id.push(b.asset_id)
                    : a.push({ ...b, asset_id: [b.asset_id] }),
                a
            );
        }, []);
        for (let prop of groupedProps) {
            let count = new Set(prop.asset_id);
            prop.asset_id = Array.from(count);
            prop.count = prop.asset_id.length;
        }
        return groupedProps;
    };

    const groupPropsByKindAndName = (props) => {
        let groupedProps = props.reduce((a, b) => {
            const found = a.find((e) => e.kind == b.kind && e.name == b.name);
            return (
                found
                    ? found.asset_id.push(b.asset_id)
                    : a.push({ ...b, asset_id: [b.asset_id] }),
                a
            );
        }, []);
        for (let prop of groupedProps) {
            let count = new Set(prop.asset_id);
            prop.asset_id = Array.from(count);
            prop.count = prop.asset_id.length;
            prop.label = prop.kind + " - " + prop.name;
        }
        return groupedProps;
    };
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">
                    {`${$format("page.properties.asset_props_loc")} ${loc}`}
                </h1>
            </div>
            <div class="col-6">
                <BreadCrumNav
                    routes={[
                        {
                            label: $format("page.properties.asset_props"),
                            path: "/property/asset",
                        },
                        { label: $format("page.properties.asset_props") },
                    ]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12 d-flex">
                <div class="card mt-3 card-secondary card-outline no-export col-12 w-100">
                    <div class="card-header">
                        <h4 class="card-title">
                            {$format("components.advancedSearch.search")}
                        </h4>
                    </div>
                    <div class="card-body pe-5">
                        <div class="row">
                            <div class="col-12 col-md-6 mb-3">
                                <label for="asset__kind__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.asset_kind")}
                                </label>

                                <!-- SVELTE 5 UPDATE: Replaced Select2 with TomSelect -->
                                <select
                                    id="asset__kind__in"
                                    name="asset__kind__in"
                                    multiple
                                    use:tomSelectAction={{
                                        options: translatedAssetKinds,
                                        value: filters.asset__kind__in,
                                        placeholder: $format("buttons.search"),
                                        onChange: (val) => (filters.asset__kind__in = val),
                                    }}
                                ></select>
                            </div>

                            <div class="col-12 col-md-6 mb-3">
                                {#await getTags()}
                                    <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                        {$format("components.advancedSearch.asset_tag")}
                                    </label>
                                    <select class="form-select" name="asset_tag_id" disabled>
                                        <option value="">--</option>
                                    </select>
                                {:then tags}
                                    <label for="asset_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                        {$format("components.advancedSearch.asset_tag")}
                                    </label>

                                    <!-- SVELTE 5 UPDATE: Standard BS5 select -->
                                    <select
                                        class="form-select"
                                        name="asset_tag_id"
                                        bind:value={filters.asset_tag_id}
                                    >
                                        <option value="">--</option>
                                        {#each tags as opt}
                                            <option value={opt.id}>
                                                {opt.name}
                                            </option>
                                        {/each}
                                    </select>
                                {/await}
                            </div>
                        </div>

                        <div class="btn-strip pt-3 mt-2 border-top">
                            <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleFilterChange}>
                                <i class="fas fa-search me-1"></i> {$format("buttons.search")}
                            </button>
                            <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
                                <i class="fas fa-eraser me-1"></i> {$format("buttons.reset")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {#key refresh + JSON.stringify(currentFilter)}
            {#await getData()}
                <div class="card mt-3 w-100" style="min-height: 500px;">
                    <div class="card-body" style="display: flex; justify-content: center;">
                        <div class="overlay">
                            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                        </div>
                    </div>
                </div>
            {:then data}
                <div class="row">
                    <div class="col-12 d-flex flex-wrap flex-md-nowrap w-100" style="gap: 15px;">
                        <div class="card mt-3 w-100" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title">
                                    {$format("page.properties.assets_per_prop_name")}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if data.data.length > 0}
                                    <AssetCount
                                        data={data.propsByKind}
                                        field={"kind"}
                                        filter={currentFilter}
                                    />
                                {:else}
                                    <AssetCount
                                        data={[]}
                                        field={"kind"}
                                        filter={currentFilter}
                                    />
                                {/if}
                            </div>
                        </div>
                        <div class="card mt-3 w-100" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title">
                                    {$format("page.properties.assets_per_prop_kind_name")}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if data.data.length > 0}
                                    <CountByPropValue
                                        data={data.propsByKindAndName}
                                        field={"value"}
                                        type={"asset"}
                                        propertyKind={data.propsByKindAndName[0].kind}
                                        linkTo={"name"}
                                    />
                                {:else}
                                    <CountByPropValue
                                        data={[]}
                                        field={"value"}
                                        type={"asset"}
                                        propertyKind={""}
                                        linkTo={"name"}
                                    />
                                {/if}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mt-3">
                    <div class="col-12 w-100">
                        <section class="content px-0">
                            <div class="container-fluid px-0">
                                <div class="row">
                                    <div class="col-12 w-100">
                                        <!-- Custom Event Dispatcher kept to on:refresh to maintain compatibility with child component -->
                                        <PropertiesList
                                            data={data.data}
                                            {columns}
                                            exportsName="Propiedades-de-activo-por-ubicacion"
                                            parseFunc={parseRows}
                                            on:refresh={() => (refresh = !refresh)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            {/await}
        {/key}
    </div>
</section>

<style>
    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }
</style>